import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Box, Grid, useMediaQuery } from "@mui/material";
import { setDialog } from "../../../features/dialog/dialogSlice";
import {
  useCancelMyShopOrderMutation,
  useDownloadMyShopOrderInvoiceMutation,
  useGetMyShopOrdersQuery,
} from "../../../features/shop/shopApiSlice";
import {
  getProductCategoryLabels,
  getActivePromotion,
  getProductCategoryKey,
  getUniqueProductCategories,
  normalizeProductCategories,
} from "./shopUtils";
import ShopCartPanel from "./ShopCartPanel";
import ShopConfirmDialog from "./ShopConfirmDialog";
import ShopCatalogMemberHeader from "./ShopCatalogMemberHeader";
import ShopCatalogOrdersView from "./ShopCatalogOrdersView";
import {
  ShopCatalogProductGridView,
  ShopCatalogProductListView,
} from "./ShopCatalogProductViews";
import {
  getInitialMemberCatalogLayout,
  MEMBER_CATALOG_LAYOUT_STORAGE_KEY,
  mapOrderLineItemsToCartItems,
} from "./shopCatalogHelpers";

export default function ShopCatalogTab({
  catalogProducts = [],
  isMemberCatalogView,
  hasContractContext,
  checkoutDisabledMessage,
  checkoutContextNote,
  loadingProducts,
  loadingAccess,
  onSelectProduct,
  cartItems,
  setCartItems,
  cartTotal,
  cartTotalItems,
  onCheckout,
  creatingOrder,
  variantInventoryByKey,
}) {
  const dispatch = useDispatch();
  const isMobileViewport = useMediaQuery((theme) =>
    theme.breakpoints.down("sm")
  );
  const isLoading = loadingProducts || loadingAccess;
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [memberViewMode, setMemberViewMode] = useState("catalog");
  const [memberCatalogLayout, setMemberCatalogLayout] = useState(
    getInitialMemberCatalogLayout
  );
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [activeOrderActionId, setActiveOrderActionId] = useState("");
  const [activeInvoiceOrderId, setActiveInvoiceOrderId] = useState("");
  const [confirmState, setConfirmState] = useState({
    open: false,
    type: "cancel",
    order: null,
  });

  const isMyOrdersView = isMemberCatalogView && memberViewMode === "orders";
  const isListLayout = isMemberCatalogView && memberCatalogLayout === "list";
  const referenceDate = useMemo(() => new Date(nowTs), [nowTs]);

  const {
    data: myOrders = [],
    isLoading: loadingMyOrders,
    isFetching: fetchingMyOrders,
  } = useGetMyShopOrdersQuery(undefined, {
    skip: !isMemberCatalogView,
  });

  const [cancelMyShopOrder] = useCancelMyShopOrderMutation();
  const [downloadMyShopOrderInvoice] = useDownloadMyShopOrderInvoiceMutation();

  const hasLivePromotion = useMemo(
    () =>
      catalogProducts.some((product) =>
        Boolean(getActivePromotion(product, referenceDate))
      ),
    [catalogProducts, referenceDate]
  );
  const categoryOptions = useMemo(
    () => getUniqueProductCategories(catalogProducts),
    [catalogProducts]
  );
  const filteredCatalogProducts = useMemo(() => {
    const selectedCategoryKeys = new Set(
      normalizeProductCategories(selectedCategories).map((category) =>
        getProductCategoryKey(category)
      )
    );
    if (!selectedCategoryKeys.size) return catalogProducts;

    return catalogProducts.filter(
      (product) =>
        getProductCategoryLabels(product).some((category) =>
          selectedCategoryKeys.has(getProductCategoryKey(category))
        )
    );
  }, [catalogProducts, selectedCategories]);
  const emptyStateTitle = selectedCategories.length
    ? "Keine Produkte in den ausgewaehlten Kategorien verfuegbar."
    : "Noch keine aktiven Produkte verfuegbar.";
  const emptyStateDescription = selectedCategories.length
    ? "Waehle andere Kategorien oder entferne den Filter."
    : "Schau bald wieder vorbei.";

  useEffect(() => {
    if (!isMemberCatalogView) {
      setMemberViewMode("catalog");
      setExpandedOrderId(null);
      setConfirmState({ open: false, type: "cancel", order: null });
    }
  }, [isMemberCatalogView]);

  useEffect(() => {
    if (!hasLivePromotion) return undefined;

    const intervalId = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [hasLivePromotion]);

  useEffect(() => {
    try {
      localStorage.setItem(
        MEMBER_CATALOG_LAYOUT_STORAGE_KEY,
        memberCatalogLayout
      );
    } catch {
      // ignore storage access problems
    }
  }, [memberCatalogLayout]);

  useEffect(() => {
    if (!selectedCategories.length) return;

    const availableCategoryKeys = new Set(
      categoryOptions.map((category) => getProductCategoryKey(category))
    );
    setSelectedCategories((previousCategories) => {
      const nextCategories = previousCategories.filter((category) =>
        availableCategoryKeys.has(getProductCategoryKey(category))
      );
      return nextCategories.length === previousCategories.length
        ? previousCategories
        : nextCategories;
    });
  }, [categoryOptions, selectedCategories.length]);

  const closeConfirmDialog = () =>
    setConfirmState({
      open: false,
      type: "cancel",
      order: null,
    });

  const handleCancelOrder = async (order, { silentSuccess = false } = {}) => {
    if (!order?._id) return false;

    setActiveOrderActionId(order._id);
    try {
      await cancelMyShopOrder(order._id).unwrap();

      if (!silentSuccess) {
        dispatch(
          setDialog({
            type: "SUCCESS",
            title: "Bestellung storniert",
            data: {
              successText: "Deine Bestellung wurde erfolgreich storniert.",
            },
          })
        );
      }

      return true;
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Stornierung fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Die Bestellung konnte nicht storniert werden.",
          },
        })
      );
      return false;
    } finally {
      setActiveOrderActionId("");
    }
  };

  const handleCorrectOrder = async (order) => {
    if (!order?._id) return;

    const correctedCartItems = mapOrderLineItemsToCartItems(order.lineItems);
    if (!correctedCartItems.length) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Korrektur nicht möglich",
          data: {
            errorText:
              "Die Bestellung enthält keine korrigierbaren Positionen.",
          },
        })
      );
      return;
    }

    const canceled = await handleCancelOrder(order, { silentSuccess: true });
    if (!canceled) return;

    setCartItems(correctedCartItems);
    setMemberViewMode("catalog");

    dispatch(
      setDialog({
        type: "SUCCESS",
        title: "Bestellung zur Korrektur geöffnet",
        data: {
          successText:
            "Die alte Bestellung wurde storniert und in deinen Warenkorb übernommen. Du kannst sie jetzt anpassen und neu bestellen.",
        },
      })
    );
  };

  const handleConfirmAction = async () => {
    const order = confirmState.order;
    const type = confirmState.type;
    closeConfirmDialog();

    if (!order?._id) return;

    if (type === "correct") {
      await handleCorrectOrder(order);
      return;
    }

    await handleCancelOrder(order);
  };

  const handleDownloadInvoice = async (order) => {
    if (!order?._id) return;

    setActiveInvoiceOrderId(order._id);
    try {
      const objectUrl = await downloadMyShopOrderInvoice(order._id).unwrap();
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `rechnung-${order._id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "Rechnung konnte nicht geladen werden",
          data: {
            errorText:
              error?.data?.message ||
              "Die Rechnung steht aktuell noch nicht zur Verfügung.",
          },
        })
      );
    } finally {
      setActiveInvoiceOrderId("");
    }
  };

  const handleMemberCatalogLayoutChange = (_, nextLayout) => {
    if (!nextLayout) return;
    setMemberCatalogLayout(nextLayout);
  };

  const handleToggleExpandedOrder = (orderId) => {
    setExpandedOrderId((previousOrderId) =>
      previousOrderId === orderId ? null : orderId
    );
  };

  const openCancelConfirm = (order) => {
    setConfirmState({
      open: true,
      type: "cancel",
      order,
    });
  };

  const openCorrectConfirm = (order) => {
    setConfirmState({
      open: true,
      type: "correct",
      order,
    });
  };

  return (
    <Grid
      container
      id="shopCatalogTab"
      spacing={{ xs: 2, md: 3 }}
      sx={{ maxWidth: "100%", p: 0, m: 0 }}
    >
      <Grid
        item
        xs={12}
        md={isMyOrdersView ? 12 : 8}
        sx={{ minWidth: 0, pl: "0 !important" }}
      >
        {isMemberCatalogView ? (
          <ShopCatalogMemberHeader
            isMobileViewport={isMobileViewport}
            isMyOrdersView={isMyOrdersView}
            catalogProductsCount={filteredCatalogProducts.length}
            totalCatalogProductsCount={catalogProducts.length}
            myOrdersCount={myOrders.length}
            hasLivePromotion={hasLivePromotion}
            loadingMyOrders={loadingMyOrders}
            fetchingMyOrders={fetchingMyOrders}
            memberCatalogLayout={memberCatalogLayout}
            categoryOptions={categoryOptions}
            selectedCategories={selectedCategories}
            onShowCatalog={() => setMemberViewMode("catalog")}
            onShowOrders={() => setMemberViewMode("orders")}
            onLayoutChange={handleMemberCatalogLayoutChange}
            onCategoryChange={(nextValue) =>
              setSelectedCategories(normalizeProductCategories(nextValue))
            }
          />
        ) : null}

        {isMemberCatalogView && !hasContractContext && !isMyOrdersView ? (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Dein Konto hat keinen verknuepften Vertrag. Der Checkout ist
            deaktiviert.
          </Alert>
        ) : null}

        {isMyOrdersView ? (
          <ShopCatalogOrdersView
            myOrders={myOrders}
            loadingMyOrders={loadingMyOrders}
            activeOrderActionId={activeOrderActionId}
            activeInvoiceOrderId={activeInvoiceOrderId}
            expandedOrderId={expandedOrderId}
            onToggleExpanded={handleToggleExpandedOrder}
            onOpenCancelConfirm={openCancelConfirm}
            onOpenCorrectConfirm={openCorrectConfirm}
            onDownloadInvoice={handleDownloadInvoice}
          />
        ) : isListLayout ? (
          <ShopCatalogProductListView
            catalogProducts={filteredCatalogProducts}
            isLoading={isLoading}
            onSelectProduct={onSelectProduct}
            referenceDate={referenceDate}
            emptyStateTitle={emptyStateTitle}
            emptyStateDescription={emptyStateDescription}
          />
        ) : (
          <ShopCatalogProductGridView
            catalogProducts={filteredCatalogProducts}
            isLoading={isLoading}
            isMemberCatalogView={isMemberCatalogView}
            hasContractContext={hasContractContext}
            onSelectProduct={onSelectProduct}
            referenceDate={referenceDate}
            emptyStateTitle={emptyStateTitle}
            emptyStateDescription={emptyStateDescription}
          />
        )}
      </Grid>

      {!isMyOrdersView ? (
        <Grid item xs={12} md={4} sx={{ display: { xs: "none", md: "block" } }}>
          <Box sx={{ position: "sticky", top: 88 }}>
            <ShopCartPanel
              cartItems={cartItems}
              setCartItems={setCartItems}
              cartTotal={cartTotal}
              cartTotalItems={cartTotalItems}
              onCheckout={onCheckout}
              creatingOrder={creatingOrder}
              hasContractContext={hasContractContext}
              checkoutDisabledMessage={checkoutDisabledMessage}
              checkoutContextNote={checkoutContextNote}
              isMemberCatalogView={isMemberCatalogView}
              variantInventoryByKey={variantInventoryByKey}
            />
          </Box>
        </Grid>
      ) : null}

      <ShopConfirmDialog
        open={confirmState.open}
        title={
          confirmState.type === "correct"
            ? "Bestellung korrigieren"
            : "Bestellung stornieren"
        }
        message={
          confirmState.type === "correct"
            ? `Diese Bestellung wird storniert und in deinen Warenkorb übernommen.${
                cartItems.length
                  ? " Dein aktueller Warenkorb wird dabei ersetzt."
                  : ""
              }`
            : "Möchtest du diese Bestellung wirklich stornieren?"
        }
        confirmLabel={
          confirmState.type === "correct" ? "Korrigieren" : "Stornieren"
        }
        confirmColor={confirmState.type === "correct" ? "primary" : "warning"}
        onConfirm={handleConfirmAction}
        onCancel={closeConfirmDialog}
        loading={Boolean(activeOrderActionId)}
      />
    </Grid>
  );
}
